import { useEffect, useState } from 'react';
import { Capacitor } from '@capacitor/core';
import { SfmcMobilePush, type SfmcInboxMessage } from 'capacitor-sfmc-mobile-push';
import { buildDummyInboxMessages } from '@/lib/notifications/dummyInboxMessages';

export type NotificationBellStatus = 'empty' | 'read' | 'unread';

function statusFromMessages(messages: SfmcInboxMessage[]): NotificationBellStatus {
  const active = messages.filter(m => !m.isDeleted);
  if (active.length === 0) return 'empty';
  return active.some(m => !m.isRead) ? 'unread' : 'read';
}

/**
 * Derives the notification bell's visual state from the Marketing Cloud Inbox — drives
 * whether it renders empty, read, or unread. Falls back to the same dummy data as the
 * notifications inbox screen in browser dev (see `dummyInboxMessages`), since the web plugin
 * stub always resolves `getInboxMessages` to an empty list.
 */
export function useNotificationBellStatus(): NotificationBellStatus {
  const [status, setStatus] = useState<NotificationBellStatus>('empty');

  useEffect(() => {
    let isStale = false;

    if (!Capacitor.isNativePlatform()) {
      setStatus(statusFromMessages(buildDummyInboxMessages(Date.now())));
      return;
    }

    SfmcMobilePush.getInboxMessages()
      .then(({ messages }) => {
        if (!isStale) setStatus(statusFromMessages(messages));
      })
      .catch(err => {
        console.warn('[useNotificationBellStatus] getInboxMessages failed:', err);
      });

    return () => {
      isStale = true;
    };
  }, []);

  return status;
}
